/**
 * True when the event is targeting UI rendered in a MUI portal (pickers, Select/Menu, Dialog)
 * so document-level click-away / collapse logic should not treat it as "outside" the slot.
 */
export function isMuiPortalDismissExemptTarget(event: MouseEvent | TouchEvent): boolean {
  const first = event.target;
  if (!first || !(first instanceof Element)) {
    return false;
  }

  const classString = (el: Element): string => {
    if (el instanceof HTMLElement) {
      return typeof el.className === 'string' ? el.className : '';
    }
    return el.getAttribute('class') ?? '';
  };

  const nodeExempt = (node: Element): boolean => {
    const cn = classString(node);
    if (
      cn.includes('MuiPickersPopper') ||
      cn.includes('MuiPickersLayout') ||
      cn.includes('MuiTimePicker') ||
      (cn.includes('MuiPopper') && node.getAttribute('role') === 'dialog') ||
      cn.includes('MuiPopover-root') ||
      cn.includes('MuiMenu-root') ||
      cn.includes('MuiMenu-paper') ||
      cn.includes('MuiPopover-paper') ||
      cn.includes('MuiModal-root') ||
      cn.includes('MuiDialog-root') ||
      cn.includes('MuiDialog-container')
    ) {
      return true;
    }
    const role = node.getAttribute('role');
    return role === 'listbox' || role === 'option' || role === 'menu' || role === 'menuitem';
  };

  let el: Element | null = first;
  while (el) {
    if (nodeExempt(el)) {
      return true;
    }
    el = el.parentElement;
  }

  const path = (event as Event & { composedPath?: () => EventTarget[] })?.composedPath?.();
  if (path && Array.isArray(path)) {
    for (const node of path) {
      if (node instanceof Element && nodeExempt(node)) {
        return true;
      }
    }
  }

  return false;
}
